// Libraries
import React from 'react';

// Supermove
import {Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useState} from '@supermove/hooks';
import {ProjectModel, UserModel} from '@supermove/models';

// App
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import FinalizedInvoiceCallout from '@shared/modules/Accounting/components/Invoice/FinalizedInvoiceCallout';
import ProjectBilling from '@shared/modules/Project/components/Billing/ProjectBilling';
import ProjectBillingActionButtons from '@shared/modules/Project/components/Billing/ProjectBillingActionButtons';
import ProjectDetailsBlockHeader, {
  BlockHeaderLoader,
} from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';
import {ProjectBlockKindType} from '@shared/modules/Project/enums/ProjectBlockKind';

const BLOCK_TITLE = 'Billing';

const SkeletonComponent = () => {
  return (
    <React.Fragment>
      <BlockHeaderLoader title={BLOCK_TITLE} />
      <SkeletonLoader isFullWidth height={36} />
      <Space height={16} />
      <ProjectBilling.SkeletonLoader />
    </React.Fragment>
  );
};

const ProjectDetailsBillingBlockContent = ({
  project,
  refetch,
  pageRefetch,
  refetchAndReset,
  handleUpdateBlock,
  descriptionField,
  getTruncatedKey,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  isExpandAllBills,
  setIsExpandAllBills,
  viewer,
  isWidget,
}: {
  project: ProjectModel;
  refetch: () => void;
  pageRefetch: () => void;
  refetchAndReset: () => void;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
  descriptionField: string;
  getTruncatedKey: ({billItemId}: {billItemId: string}) => string;
  truncated: Record<string, boolean>;
  setTruncated: (truncated: Record<string, boolean>) => void;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: (enabledToggle: Record<string, boolean>) => void;
  isExpandAllBills: boolean;
  setIsExpandAllBills: (isExpanded: boolean) => void;
  viewer: UserModel;
  isWidget?: boolean;
}) => {
  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader
        title={`Billing`}
        ActionsComponent={ProjectBillingActionButtons}
        actionsComponentProps={{
          project,
          refetch,
          pageRefetch,
          refetchAndReset,
          handleUpdateBlock,
          invoiceActionSections: ['BILLING', 'PAYMENTS'],
        }}
      />
      <FinalizedInvoiceCallout
        invoice={project.mainMoveInvoice}
        refetch={refetch}
        isAuthorizedAccountingActions={viewer.isAuthorizedAccountingActions}
      />
      <ProjectBilling
        project={project}
        refetch={refetch}
        pageRefetch={pageRefetch}
        descriptionField={descriptionField}
        getTruncatedKey={getTruncatedKey}
        truncated={truncated}
        setTruncated={setTruncated}
        enabledToggle={enabledToggle}
        setEnabledToggle={setEnabledToggle}
        isExpandAllBills={isExpandAllBills}
        setIsExpandAllBills={setIsExpandAllBills}
        isWidget={isWidget}
      />
    </React.Fragment>
  );
};

const ProjectDetailsBillingBlock = ({
  project,
  projectUuid,
  index,
  pageRefetch,
  refetchAndReset,
  handleUpdateBlock,
}: {
  project?: ProjectModel;
  projectUuid: string;
  index: number;
  pageRefetch: () => void;
  refetchAndReset: () => void;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
}) => {
  // Note(shawn): Defining expand/collapse state for bill item descriptions here to avoid state
  // being reset on remounts whenever page height changes.
  const descriptionField = 'description';
  const getTruncatedKey = ({billItemId}: {billItemId: string}) =>
    `BILL_ITEM_${billItemId}_${descriptionField.toUpperCase()}`;
  const [truncated, setTruncated] = useState({});
  const [enabledToggle, setEnabledToggle] = useState({});
  const [isExpandAllBills, setIsExpandAllBills] = useState(true);

  return (
    <ProjectDetailsBlockWrapper
      key={`${projectUuid}_${project?.billsHash}_${project?.jobStopsHash}_${project?.totalRevenue}_${project?.valuationCoverage?.updatedAt}`}
      query={ProjectDetailsBillingBlock.query}
      queryVariables={{projectUuid}}
      index={index}
      SkeletonComponent={SkeletonComponent}
    >
      {({
        data,
        refetch,
      }: {
        data: {project: ProjectModel; viewer: UserModel};
        refetch: () => void;
      }) => {
        return (
          <ProjectDetailsBillingBlockContent
            project={data.project}
            handleUpdateBlock={handleUpdateBlock}
            refetch={refetch}
            pageRefetch={pageRefetch}
            refetchAndReset={refetchAndReset}
            descriptionField={descriptionField}
            getTruncatedKey={getTruncatedKey}
            truncated={truncated}
            setTruncated={setTruncated}
            enabledToggle={enabledToggle}
            setEnabledToggle={setEnabledToggle}
            isExpandAllBills={isExpandAllBills}
            setIsExpandAllBills={setIsExpandAllBills}
            viewer={data.viewer}
          />
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsBillingBlock.fragment = gql`
  fragment ProjectDetailsBillingBlock on Project {
    id
    billsHash
    totalRevenue
    jobStopsHash
    valuationCoverage {
      id
      updatedAt
    }
  }
`;

ProjectDetailsBillingBlock.query = gql`
  ${ProjectBillingActionButtons.fragment}
  ${FinalizedInvoiceCallout.fragment}
  ${ProjectBilling.fragment}

  query ProjectDetailsBillingBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      uuid
      bills {
        id
        billItems {
          id
        }
      }
      mainMoveInvoice {
        id
        ...FinalizedInvoiceCallout
      }
      ...ProjectBillingActionButtons
      ...ProjectBilling
    }
    viewer {
      id
      isAuthorizedAccountingActions
    }
  }
`;

export default ProjectDetailsBillingBlock;
